import { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import {useCompanies, useDoctors, useTemplates, useGetTemplatesForDoctor} from "./hooks"
import { createDoctorNoteTemplateMapping, deleteDoctorTemplates, createNoteTemplateMapping, uploadSpecialtyTemplate } from "./services/masterDataService";
import { getUsersByCompany } from "./services/masterDataService";
import { useCurrentDomain } from "./context/DomainContext";
import apiClient from "./api/client"; 

const RequiredStar = () => <span style={{ color: "red" }}> *</span>;

const TemplateConfigurationContent = ({ activeTab }) => {
  const { domain } = useCurrentDomain();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { companies } = useCompanies(domain);
  const [selectedDoctors, setSelectedDoctors] = useState([]); 
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [doctorSearchInput, setDoctorSearchInput] = useState("");
  const { doctors, fullDoctors, loading: doctorsLoading } = useDoctors(
    selectedCompany?.value,
    domain
  );
  const [selectedSpeciality, setSelectedSpeciality] = useState(null);
  const [specialities, setSpecialities] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState([]);
  const templates = useTemplates(domain);
  const [doctorTemplates, setDoctorTemplates] = useState([]);

  const [selectedTemplatesToDelete, setSelectedTemplatesToDelete] = useState([]);

  const [selectedNoteTemplate, setSelectedNoteTemplate] = useState(null);
  const [jsonFile, setJsonFile] = useState(null);
  const [docxFile, setDocxFile] = useState(null);
  const [checkboxes, setCheckboxes] = useState({
    doctorSignature: false,
    patientSignature: false,
    technicianSignature: false,
    diagnosisCode: false,
    procedureCode: false,
  });

  const [tab3Specialities, setTab3Specialities] = useState([]);
  const [tab3NoteTemplates, setTab3NoteTemplates] = useState([]);
  const [tab3SpecialitiesLoading, setTab3SpecialitiesLoading] = useState(false);
  const [tab3TemplatesLoading, setTab3TemplatesLoading] = useState(false);

  const [newTemplateName, setNewTemplateName] = useState("");
  const [newVisitType, setNewVisitType] = useState(null);
  const [newNoteTypeId, setNewNoteTypeId] = useState(null);
  const [userDoctors, setUserDoctors] = useState([]);

  const [errors, setErrors] = useState({});
  const jsonFileRef = useRef(null);
  const docxFileRef = useRef(null);

  const handleSelectChange = (setter) => (selected) => {
    setter(selected);
    setErrors((prev) => ({ ...prev, [setter.name || "unknown"]: null }));
  };

  const handleTab1CompanyChange = (selected) => {
    setSelectedCompany(selected);
    setSelectedDoctors([]);
    setSelectedSpeciality(null);
    setSelectedTemplate(null);
    setSpecialities([]);
    setErrors((prev) => ({ ...prev, company: null, doctor: null, speciality: null, template: null }));
  };

  const handleTab1DoctorChange = (selected) => {
    const newSelected = Array.isArray(selected) ? selected : [];
    if (newSelected.length === 0) {
      setSelectedDoctors([]);
      setSelectedSpeciality(null);
      setSelectedTemplate(null);
      setSpecialities([]);
      setErrors((prev) => ({ ...prev, doctor: null, speciality: null, template: null }));
      return;
    }

    setSelectedDoctors(newSelected);
    setSelectedTemplate(null);
    setErrors((prev) => ({ ...prev, doctor: null, template: null }));

    const allProcedures = newSelected.flatMap(doc => doc.procedures || []);

    const uniqueProcedures = Array.from(new Map(
      allProcedures.map(p => [p.szProcedureGroupId, p])
    ).values());

    if (uniqueProcedures.length === 1) {
      const autoSpeciality = {
        value: uniqueProcedures[0].szProcedureGroupId,
        label: uniqueProcedures[0].procedureGroupName || uniqueProcedures[0].szProcedureGroupId
      };
      setSelectedSpeciality(autoSpeciality);
    } else if (uniqueProcedures.length > 1) {
      setErrors(prev => ({ ...prev, doctor: "Doctors from multiple specialities are not allowed" }));
      setSelectedSpeciality(null);
    } else {
      setSelectedSpeciality(null);
    }

    const procedureOptions = uniqueProcedures.map(p => ({
      value: p.szProcedureGroupId,
      label: p.procedureGroupName || p.szProcedureGroupId,
    })).sort((a, b) => a.label.localeCompare(b.label));

    setSpecialities(procedureOptions);
  };

  const handleTab1SpecialityChange = (selected) => {
    setSelectedSpeciality(selected);
    setSelectedTemplate(null);
    setErrors((prev) => ({ ...prev, speciality: null, template: null }));
  };

  const handleTab2CompanyChange = async (selected) => {
    setSelectedCompany(selected);
    setSelectedUser(null);
    setSelectedDoctors([]);
    setDoctorTemplates([]);
    setSelectedTemplatesToDelete([]);
    setUserDoctors([]);
    setErrors((prev) => ({ ...prev, company: null, doctor: null, user: null }));

    if (selected?.value) {
      try {
        const userList = await getUsersByCompany(domain, selected.value);
        setUsers(userList.map(u => ({
          value: u.userId,
          label: `${u.userName} (${u.userRole})`
        })));
      } catch (err) {
        console.error(err);
        setUsers([]);
      }
    } else {
      setUsers([]);
    }
  };

  const handleTab2UserChange = async (selected) => {
    setSelectedUser(selected);
    setSelectedTemplatesToDelete([]);

    if (!selected || !selectedCompany?.value) {
      setSelectedDoctors([]);
      setUserDoctors([]);
      return;
    }

    try {
      const res = await apiClient.get(
        `/api/doctors-by-user?domain=${encodeURIComponent(domain)}&userId=${encodeURIComponent(selected.value)}&companyId=${encodeURIComponent(selectedCompany.value)}`
      );
      
      const doctorsForUser = res.data || [];
      setUserDoctors(doctorsForUser);

      const userDoctorIds = new Set(doctorsForUser.map(d => d.szDoctorId));
      const preSelected = doctors.filter(doc => userDoctorIds.has(doc.szDoctorId || doc.value));

      setSelectedDoctors(preSelected);
    } catch (err) {
      console.error("Failed to fetch doctors for user", err);
      toast.error("Failed to load doctors for selected user. You can still select manually.");
      setUserDoctors([]);
    }
  };

  const handleTab2DoctorChange = (selected) => {
    const newSelected = Array.isArray(selected) ? selected : selected ? [selected] : [];
    setSelectedDoctors(newSelected);
    setSelectedTemplatesToDelete([]);
    setErrors((prev) => ({ ...prev, doctor: null }));
  };

  useEffect(() => {
    if (activeTab !== 2 || !selectedCompany?.value || !selectedDoctors.length) {
      setDoctorTemplates([]);
      setSelectedTemplatesToDelete([]);
      return;
    }

    const loadTemplates = async () => {
      try {
        const doctorIds = selectedDoctors.map(d => d.value).join(',');
        const res = await apiClient.get(
          `/api/doctor-note-templates-with-doctor-info?domain=${encodeURIComponent(domain)}&doctorIds=${encodeURIComponent(doctorIds)}`
        );
        setDoctorTemplates(res.data || []);
      } catch (err) {
        console.error("Failed to fetch doctor templates", err);
        setDoctorTemplates([]);
        toast.error("Failed to load templates");
      }
    };

    loadTemplates();
  }, [selectedDoctors, activeTab, domain, refreshKey, selectedCompany]);

  useEffect(() => {
    if (activeTab !== 1 || !selectedCompany?.value || !selectedDoctors.length) {
      return;
    }

    const loadTemplatesForTab1 = async () => {
      try {
        const doctorIds = selectedDoctors.map(d => d.value).join(',');
        const res = await apiClient.get(
          `/api/doctor-note-templates-with-doctor-info?domain=${encodeURIComponent(domain)}&doctorIds=${encodeURIComponent(doctorIds)}`
        );
        setDoctorTemplates(res.data || []);
      } catch (err) {
        console.error("Failed to fetch doctor templates for Tab1", err);
        setDoctorTemplates([]);
      }
    };

    loadTemplatesForTab1();
  }, [selectedDoctors, activeTab, domain, selectedCompany]);

  const handleFileChange = (setter) => (e) => {
    const file = e.target.files[0];
    if (file) {
      setter(file);
      setErrors((prev) => ({ ...prev, [setter.name || "file"]: null }));
    }
  };

  const handleCheckboxChange = (key) => {
    setCheckboxes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allSelected =
    doctorTemplates.length > 0 &&
    doctorTemplates.every((t) => {const key = `${t.doctorId || t.doctorId}-${t.templateId}`;
      return selectedTemplatesToDelete.includes(key);
    });

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedTemplatesToDelete([]);
    } else {
      const allKeys = doctorTemplates.map(t => `${t.doctorId || t.doctorId}-${t.templateId}`);
      setSelectedTemplatesToDelete(allKeys);
    }
  };

  const toggleTemplateDelete = (doctorId, templateId) => {
    const key = `${doctorId}-${templateId}`;
    setSelectedTemplatesToDelete((prev) =>
      prev.includes(key) ? prev.filter((v) => v !== key) : [...prev, key]
    );
  };

  const confirmAndDeleteTemplates = () => {
    if (selectedTemplatesToDelete.length === 0) {
      toast.warn("No templates selected");
      return;
    }

    const count = selectedTemplatesToDelete.length;
    const toastId = toast(
      <div style={{ 
        textAlign: "center", 
        minWidth: "300px",
        padding: "20px 10px"
      }}>
        <div style={{ 
          fontWeight: "600", 
          marginBottom: "15px",
          fontSize: "16px"
        }}>
          Delete {count} template assignment(s)?
        </div>

        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          marginTop: "10px"
        }}>
          <button
            className="btn btn-sm btn-outline-secondary px-4"
            onClick={() => toast.dismiss(toastId)}
          >
            Cancel
          </button>

          <button
            className="btn btn-sm btn-danger px-4"
            onClick={() => {
              toast.dismiss(toastId);
              performDelete();
            }}
          >
            Yes, Delete
          </button>
        </div>
      </div>,
      {
        autoClose: false,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
        position: "top-center", 
        className: "bg-white shadow border-0",
        style: {
          marginTop: "100px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)"
        }
      }
    );
  };

  const performDelete = async () => {
    if (!Array.isArray(selectedDoctors) || selectedDoctors.length === 0) return;    
    let loadingToast = null;
    try {
      loadingToast = toast.loading("Deleting templates...");
      
      const deletePairs = selectedTemplatesToDelete.map(key => {
        const [doctorId, templateId] = key.split('-');
        return { doctorId, templateId: parseInt(templateId) };
      });
      const doctorIdParam = selectedDoctors.map(d => d.value).join(',');
      const templateIdsParam = [...new Set(deletePairs.map(p => p.templateId))].join(',');
      const response = await deleteDoctorTemplates(
        domain,
        doctorIdParam,
        templateIdsParam
      );
      toast.dismiss(loadingToast);
      toast.success(`Successfully deleted ${response?.data?.deletedTemplates?.length || 0} template(s)`);
      setSelectedTemplatesToDelete([]);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      if (loadingToast) toast.dismiss(loadingToast);
      toast.error("Failed to delete templates");
      console.error("Delete error:", err);
    }
  };

  const resetTab2 = () => {
    setSelectedCompany(null);
    setSelectedUser(null);
    setSelectedDoctors([]);
    setDoctorTemplates([]);
    setSelectedTemplatesToDelete([]);
    setUserDoctors([]);
    setUsers([]);
    setErrors({});
  };

  const validateTab1 = () => {
    const newErrors = {};
    if (!selectedCompany) newErrors.company = "Company is required";
    if (!selectedDoctors || selectedDoctors.length === 0) 
      newErrors.doctor = "At least one Doctor is required";
    if (!selectedSpeciality) newErrors.speciality = "Speciality is required";
    if (!selectedTemplate || selectedTemplate.length === 0) 
      newErrors.template = "At least one Template is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const configureTemplate = async () => {
    if (!validateTab1()) {
      toast.error("Please fill all required fields");
      return;
    }
    let loadingToast = null;
    const payload = {
      noteTemplateIds: selectedTemplate.map(t => t.value),
      doctorID: selectedDoctors.map(d => d.value),
      specialtyID: selectedSpeciality?.value,
      isActive: true,
      isNoteCarryOver: true,
    };
    try {
      loadingToast = toast.loading("Configuring template mapping...");
      await createDoctorNoteTemplateMapping(payload);
      toast.dismiss(loadingToast);
      toast.success("Template mapping created successfully!");
      resetTab1();
    } catch (err) {
      if (loadingToast) toast.dismiss(loadingToast);
      const errorMsg = err.response?.data?.message || "Failed to create template mapping";
      toast.error(errorMsg);
    }
  };

  const resetTab1 = () => {
    setSelectedCompany(null);
    setSelectedDoctors([]);
    setSelectedSpeciality(null);
    setSelectedTemplate(null);
    setSpecialities([]);
    setErrors({});
  };

  const validateTab3 = () => {
    const newErrors = {};
    if (!selectedCompany) newErrors.company = "Company is required";
    if (!selectedSpeciality) newErrors.speciality = "Speciality is required";
    if (!selectedNoteTemplate) newErrors.noteTemplate = "Note Template is required";
    if (!jsonFile) newErrors.jsonFile = "JSON/TXT file is required";
    if (!docxFile) newErrors.docxFile = "DOCX file is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const uploadTemplate = async () => {
    if (!validateTab3()) { toast.error("Please complete all required fields"); return; }
    if (!selectedCompany?.value || !selectedSpeciality?.value || !selectedNoteTemplate?.value) {
      toast.error("Required selections missing"); return;
    }
    const payload = {
      specialityName: selectedSpeciality.label,
      specialityCode: selectedSpeciality.value,
      companyId: selectedCompany.value,
      noteTemplateId: Number(selectedNoteTemplate.value),
      jsonFile,
      templateFile: docxFile,
      enableDocSign: checkboxes.doctorSignature,
      enablePatSign: checkboxes.patientSignature,
      enableTechSign: checkboxes.technicianSignature,
      enableDiagnosisCode: checkboxes.diagnosisCode,
      enableProcedureCode: checkboxes.procedureCode,
    };
    let toastId = toast.loading("Uploading template and configuration...");
    try {
      const response = await uploadSpecialtyTemplate(domain, payload);
      toast.dismiss(toastId);
      toast.success(response.message || "Template configured successfully");
      resetTab3();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to upload template");
    }
  };

  const resetTab3 = () => {
    setSelectedCompany(null); setSelectedSpeciality(null); setSelectedNoteTemplate(null);
    setJsonFile(null); setDocxFile(null);
    setTab3Specialities([]); setTab3NoteTemplates([]);
    setCheckboxes({ doctorSignature: false, patientSignature: false, technicianSignature: false, diagnosisCode: false, procedureCode: false });
    setErrors({});
    if (jsonFileRef.current) jsonFileRef.current.value = "";
    if (docxFileRef.current) docxFileRef.current.value = "";
  };

  const validateTab4 = () => {
    const newErrors = {};
    if (!newTemplateName?.trim()) newErrors.newTemplateName = "Template name is required";
    if (!newVisitType) newErrors.newVisitType = "Visit type is required";
    if (newNoteTypeId === null) newErrors.newNoteTypeId = "Note type ID is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createNewTemplate = async () => {
    if (!validateTab4()) { toast.error("Please fill all required fields"); return; }
    let loadingToast = null;
    const payload = {
      name: newTemplateName,
      visitType: newVisitType?.value, noteTypeId: newNoteTypeId?.value, isDefault: false,
    };
    try {
      loadingToast = toast.loading("Configuring new template...");
      const response = await createNoteTemplateMapping(domain, payload);
      toast.dismiss(loadingToast);
      resetTab4();
    } catch (err) {
      if (loadingToast) toast.dismiss(loadingToast);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to create template mapping");
    }
  };

  const resetTab4 = () => {
    setNewTemplateName(""); setNewVisitType(null); setNewNoteTypeId(null);
    setErrors((prev) => ({ ...prev, newTemplateName: null, newVisitType: null, newNoteTypeId: null }));
  };

  useEffect(() => {
    if (activeTab !== 3 || !selectedCompany?.value) {
      setTab3Specialities([]);
      return;
    }
    const fetchSpecialities = async () => {
      setTab3SpecialitiesLoading(true);
      try {
        const res = await apiClient.get(
      `/api/template-configuration/specialities?domain=${encodeURIComponent(domain)}&companyId=${encodeURIComponent(selectedCompany.value)}`
        );
        setTab3Specialities(
          (res.data || []).map((s) => ({ value: s.code, label: s.description }))
        );
      } catch (err) {
        console.error("Failed to fetch specialities", err);
        setTab3Specialities([]);
      } finally {
        setTab3SpecialitiesLoading(false);
      }
    };
    fetchSpecialities();
  }, [selectedCompany, activeTab, domain]);

  useEffect(() => {
    if (activeTab !== 3 || !selectedSpeciality?.value) {
      setTab3NoteTemplates([]);
      setSelectedNoteTemplate(null);
      return;
    }
    const fetchNoteTemplates = async () => {
      setTab3TemplatesLoading(true);
      try {
         const res = await apiClient.get(
      `/api/template-configuration/note-templates?domain=${encodeURIComponent(domain)}&specialityId=${encodeURIComponent(selectedSpeciality.value)}`
        );
        setTab3NoteTemplates(
          (res.data || []).map((t) => ({ value: t.id, label: `${t.id} - ${t.name}` }))
        );
      } catch (err) {
        console.error("Failed to fetch note templates", err);
        setTab3NoteTemplates([]);
      } finally {
        setTab3TemplatesLoading(false);
      }
    };
    fetchNoteTemplates();
  }, [selectedSpeciality, activeTab, domain]);

  useEffect(() => {
    setSelectedCompany(null);
    setSelectedDoctors([]);
    setSelectedSpeciality(null);
    setSelectedTemplate(null);
    setSpecialities([]);
    setSelectedTemplatesToDelete([]);
    setDoctorTemplates([]);
    setSelectedNoteTemplate(null);
    setJsonFile(null);
    setDocxFile(null);
    setCheckboxes({
      doctorSignature: false,
      patientSignature: false,
      technicianSignature: false,
      diagnosisCode: false,
      procedureCode: false,
    });
    setNewTemplateName("");
    setNewVisitType(null);
    setNewNoteTypeId(null);
    setSelectedUser(null);
    setUsers([]);
    setUserDoctors([]);
    setDoctorSearchInput("");
    setErrors({});
    if (jsonFileRef.current) jsonFileRef.current.value = "";
    if (docxFileRef.current) docxFileRef.current.value = "";
  }, [activeTab, domain]);

  useEffect(() => {
    setDoctorTemplates([]);
    setSelectedTemplatesToDelete([]);
  }, [selectedCompany, selectedDoctors]);

  const Field = ({ label, required }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
        {label}
        {required && <span style={{ color: '#dc3545', marginLeft: '3px', fontSize: '14px', lineHeight: '1' }}>*</span>}
      </span>
    </div>
  );

  const BRAND_GREEN = '#0aa375';

  const selectStyles = {
    control: (base) => ({
    ...base,
    minHeight: '30px',
    height: 'auto',          
    maxHeight: '60px',       
    overflowY: 'auto',      
    fontSize: '12px',
    borderColor: '#dee2e6',
    boxShadow: 'none',
    '&:hover': { borderColor: BRAND_GREEN },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '2px 6px',
    flexWrap: 'wrap',       
    maxHeight: '56px',
    overflowY: 'auto',
  }),
  multiValue: (base) => ({
    ...base,
    fontSize: '11px',
    maxWidth: '140px',       
  }),
  multiValueLabel: (base) => ({
    ...base,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
    option: (base, state) => ({
      ...base,
      fontSize: '12px',
      backgroundColor: state.isDisabled 
        ? '#f8f9fa' 
        : state.isSelected 
          ? BRAND_GREEN 
          : state.isFocused 
            ? '#e6f7f2' 
            : 'white',
      color: state.isDisabled ? '#adb5bd' : state.isSelected ? 'white' : '#333',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      pointerEvents: state.isDisabled ? 'none' : 'auto',
    }),
    singleValue: (base) => ({ ...base, fontSize: '12px' }),
    placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    input: (base, state) => ({
  ...base,
  color: '#333',
  fontSize: '12px',
  margin: '2px',
}),
  };

  const CustomOption = (props) => {
    const { data, isSelected, innerRef, innerProps, isDisabled } = props;

    return (
      <div 
        ref={innerRef} 
        {...innerProps} 
        style={{
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          backgroundColor: isDisabled 
            ? '#f8f9fa' 
            : isSelected 
              ? '#0aa375' 
              : props.isFocused 
                ? '#e6f7f2' 
                : 'white',
          color: isDisabled ? '#adb5bd' : isSelected ? 'white' : '#333',
          opacity: isDisabled ? 0.6 : 1,
        }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          disabled={isDisabled}
          onChange={() => {}}
          style={{ 
            marginRight: '10px', 
            accentColor: '#0aa375',
            cursor: isDisabled ? 'not-allowed' : 'pointer'
          }}
        />
        <span>{data.label}</span>
      </div>
    );
  };

  return (
   <div className="container-fluid p-0 px-1">
      <div className="card shadow-sm border-0">
        <div className="card-body p-2">
          {activeTab === 1 && (
            <>
              <div className="row g-1">
                <div className="col-12 col-md-6 col-lg-6">
                  <Field label="Company" required />
                  <Select
                    options={companies}
                    value={selectedCompany}
                    onChange={handleTab1CompanyChange}
                    placeholder="Select company..."
                    isSearchable isClearable
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                  {errors.company && <small className="text-danger">{errors.company}</small>}
                </div>

                <div className="col-12 col-md-6 col-lg-6">
                  <Field label="Doctor" required />
                  <Select
                    options={doctors.map(doc => {
                      const speciality = doc.procedures?.[0]?.procedureGroupName || 'No Specialty';
                      const isDisabled = selectedSpeciality && 
                        !doc.procedures?.some(p => p.szProcedureGroupId === selectedSpeciality.value);

                      return {
                        value: doc.szDoctorId,
                        label: `${doc.szDoctorName} - ${speciality} - ${doc.officeName || ''} - ${doc.szDoctorId}`,
                        ...doc,
                        isDisabled: isDisabled
                      };
                    })}
                    value={selectedDoctors} 
                    onChange={handleTab1DoctorChange}
                    placeholder={doctorsLoading ? "Loading doctors..." : "Search doctors..."}
                    isSearchable 
                    isClearable
                    isMulti 
                    isDisabled={!selectedCompany}
                    className="react-select-container" 
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    components={{ Option: CustomOption }}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                  />
                  {errors.doctor && <small className="text-danger">{errors.doctor}</small>}
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <Field label="Speciality" required />
                  <Select
                    options={specialities}
                    value={selectedSpeciality}
                    onChange={handleTab1SpecialityChange}
                    placeholder="Select speciality..."
                    isSearchable isClearable
                    isDisabled={!selectedDoctors}
                    className="react-select-container" 
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                  {errors.speciality && <small className="text-danger">{errors.speciality}</small>}
                </div>

                <div className="col-12 col-md-6 col-lg-8">
                  <Field label="Note Template" required />
                  <Select
                    options={templates}
                    isMulti
                    value={selectedTemplate}
                    onChange={handleSelectChange(setSelectedTemplate)}
                    placeholder="Select template..."
                    isSearchable 
                    isClearable={false}
                    isDisabled={!selectedSpeciality}
                    className="react-select-container" 
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    components={{ Option: CustomOption }}
                    closeMenuOnSelect={false} 
                    hideSelectedOptions={false} 
                  />
                  {errors.template && <small className="text-danger">{errors.template}</small>}
                </div>
              </div>

              <div className="d-flex justify-content-center gap-3 mt-2">
                <button onClick={resetTab1} className="btn btn-outline-secondary btn-sm px-3">Reset</button>
              <button onClick={configureTemplate}  className="btn btn-sm text-white px-3 fw-semibold" style={{ backgroundColor: "#0aa375", border: "none" }}>
                  Configure
                </button>
              </div>
              {selectedDoctors.length > 0 && (
                <>
                  <div className="mb-2 mt-4 fw-semibold small d-flex align-items-center justify-content-between border-top pt-3">
                    <Field label="Select templates to delete:" />
                    {doctorTemplates.length > 0 && (
                      <div className="form-check mb-0">
                        <input
                          type="checkbox"
                          id="selectAllInTab1"
                          checked={allSelected}
                          onChange={handleSelectAll}
                          style={{ accentColor: "#0aa375" }}
                        />
                        <label className="form-check-label small ms-1" htmlFor="selectAllInTab1">
                          Select all
                        </label>
                      </div>
                    )}
                  </div>

                  {doctorTemplates.length === 0 ? (
                    <div className="alert alert-info small py-2">No templates assigned to selected doctor(s)</div>
                  ) : (
                    <div className="table-responsive mb-3" style={{ maxHeight: "500px", overflowY: "auto", position: "relative", zIndex: "auto" }}>
                      <table className="table table-sm table-bordered" style={{ fontSize: "12px" }}>
                        <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 10, boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                          <tr>
                            <th style={{ width: "70px", textAlign: "center", fontWeight: "normal" }}>Select</th>
                            <th style={{ fontWeight: "normal" }}>Template Name</th>
                            <th style={{ width: "120px", fontWeight: "normal" }}>Template ID</th>
                            <th style={{ fontWeight: "normal" }}>Doctor Name </th>
                          </tr>
                        </thead>
                        <tbody>
                          {doctorTemplates
                            .sort((a, b) => String(a.doctorId || '').localeCompare(String(b.doctorId || '')))
                            .map((tmpl, index, array) => {
                              const prevDoctor = index > 0 ? array[index - 1].doctorId : null;
                              const isNewDoctorGroup = tmpl.doctorId !== prevDoctor;

                              return (
                                <tr 
                                  key={`${tmpl.doctorId}-${tmpl.templateId}-${index}`}
                                  style={{
                                    backgroundColor: isNewDoctorGroup ? '#f0f8f5' : '#ffffff',
                                    borderTop: isNewDoctorGroup ? '3px solid #0aa375' : '1px solid #dee2e6',
                                  }}
                                >
                                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                    <input
                                      type="checkbox"
                                      checked={selectedTemplatesToDelete.includes(`${tmpl.doctorId}-${tmpl.templateId}`)}
                                      onChange={() => toggleTemplateDelete(tmpl.doctorId, tmpl.templateId)}
                                      style={{ accentColor: "#0aa375" }}
                                    />
                                  </td>
                                  <td>{tmpl.templateName}</td>
                                  <td>{tmpl.templateId}</td>
                                  <td>{tmpl.doctorDisplay || `${tmpl.doctorName} (${tmpl.doctorId})`}</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {selectedTemplatesToDelete.length > 0 && (
                    <div className="d-flex justify-content-center mt-2">
                      <button
                        onClick={confirmAndDeleteTemplates}
                        className="btn btn-sm btn-danger px-4"
                      >
                        Delete Selected Templates ({selectedTemplatesToDelete.length})
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 2 && (
            <>
              <div className="row g-1 mb-2">
                <div className="col-12 col-md-4">
                  <Field label="Company" required />
                  <Select
                    options={companies}
                    value={selectedCompany}
                    onChange={handleTab2CompanyChange}
                    placeholder="Filter by company..."
                    isSearchable
                    isClearable
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <Field label="User" />
                  <Select
                    options={users}
                    value={selectedUser}
                    onChange={handleTab2UserChange}
                    placeholder="Select user (optional)..."
                    isSearchable
                    isClearable
                    isDisabled={!selectedCompany}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <Field label="Doctor" required />
                  <Select
                    options={doctors}
                    value={selectedDoctors}
                    onChange={(selected) => {
                      handleTab2DoctorChange(selected);
                      // Do NOT clear search input on selection
                    }}
                    inputValue={doctorSearchInput}
                    onInputChange={(newValue, action) => {
                      if (action.action === "input-change") {
                        setDoctorSearchInput(newValue);
                      }
                    }}
                    placeholder={doctorsLoading ? "Loading doctors..." : "Search doctors..."}
                    isSearchable
                    isClearable
                    isMulti
                    isDisabled={!selectedCompany}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    components={{ Option: CustomOption }}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                  />
                </div>
              </div>

              {selectedDoctors.length > 0 ? (
                <>
                  <div className="mb-2 fw-semibold small d-flex align-items-center justify-content-between">
                    <Field label="Select templates to delete:" />
                    {doctorTemplates.length > 0 && (
                      <div className="form-check mb-0">
                        <input
                          type="checkbox"
                          id="selectAllTemplates"
                          checked={allSelected}
                          onChange={handleSelectAll}
                          style={{ accentColor: "#0aa375" }}
                        />
                        <label className="form-check-label small ms-1" htmlFor="selectAllTemplates">
                          Select all
                        </label>
                      </div>
                    )}
                  </div>

                  {doctorTemplates.length === 0 ? (
                    <div className="alert alert-success small py-1 mb-2">No templates available</div>
                  ) : (
                    <div
                      className="table-responsive mb-3"
                      style={{ maxHeight: "500px", overflowY: "auto", position: "relative", zIndex: "auto" }}
                    >
                      <table className="table table-sm table-bordered" style={{ fontSize: "12px" }}>
                        <thead
                          style={{
                            position: "sticky",
                            top: 0,
                            background: "#fff",
                            zIndex: 10,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          <tr>
                            <th style={{ width: "70px", textAlign: "center", fontWeight: "normal" }}>Select</th>
                            <th style={{ fontWeight: "normal" }}>Template Name</th>
                            <th style={{ width: "120px", fontWeight: "normal" }}>Template ID</th>
                            <th style={{ fontWeight: "normal" }}>Doctor Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {doctorTemplates
                            .sort((a, b) => String(a.doctorId || "").localeCompare(String(b.doctorId || "")))
                            .map((tmpl, index, array) => {
                              const prevDoctor = index > 0 ? array[index - 1].doctorId : null;
                              const isNewDoctorGroup = tmpl.doctorId !== prevDoctor;

                              return (
                                <tr
                                  key={`${tmpl.doctorId}-${tmpl.templateId}-${index}`}
                                  style={{
                                    backgroundColor: isNewDoctorGroup ? "#f0f8f5" : "#ffffff",
                                    borderTop: isNewDoctorGroup ? "3px solid #0aa375" : "1px solid #dee2e6",
                                  }}
                                >
                                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                    <input
                                      type="checkbox"
                                      checked={selectedTemplatesToDelete.includes(
                                        `${tmpl.doctorId}-${tmpl.templateId}`
                                      )}
                                      onChange={() => toggleTemplateDelete(tmpl.doctorId, tmpl.templateId)}
                                      style={{ accentColor: "#0aa375" }}
                                    />
                                  </td>
                                  <td>{tmpl.templateName}</td>
                                  <td>{tmpl.templateId}</td>
                                  <td>
                                    {tmpl.doctorDisplay || `${tmpl.doctorName} (${tmpl.doctorId})`}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div className="alert alert-info small py-2 mb-2">
                  Select a company and at least one doctor to see templates
                </div>
              )}

              <div className="d-flex justify-content-center gap-3 mt-2">
                <button onClick={resetTab2} className="btn btn-outline-secondary btn-sm px-3">
                  Reset
                </button>
                <button
                  onClick={confirmAndDeleteTemplates}
                  className="btn btn-sm btn-danger px-3"
                  disabled={selectedTemplatesToDelete.length === 0}
                >
                  Delete Selected
                </button>
              </div>
            </>
          )}

          {activeTab === 3 && (
            <>
              <div className="row g-1 mb-2">
                <div className="col-12 col-md-6 col-lg-4">
                  <Field label="Company" required />
                  <Select
                    options={companies}
                    value={selectedCompany}
                    onChange={(selected) => {
                      setSelectedCompany(selected);
                      setSelectedSpeciality(null);
                      setSelectedNoteTemplate(null);
                      setTab3Specialities([]);
                      setTab3NoteTemplates([]);
                      setErrors((prev) => ({ ...prev, company: null, speciality: null, noteTemplate: null }));
                    }}
                    placeholder="Select company..."
                    isSearchable
                    isClearable
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                  {errors.company && <small className="text-danger">{errors.company}</small>}
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <Field label="Speciality" required />
                  <Select
                    options={tab3Specialities}
                    value={selectedSpeciality}
                    onChange={(selected) => {
                      setSelectedSpeciality(selected);
                      setSelectedNoteTemplate(null);
                      setTab3NoteTemplates([]);
                      setErrors((prev) => ({ ...prev, speciality: null, noteTemplate: null }));
                    }}
                    placeholder={tab3SpecialitiesLoading ? "Loading..." : "Select speciality..."}
                    isSearchable
                    isClearable
                    isDisabled={!selectedCompany || tab3SpecialitiesLoading}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                  {errors.speciality && <small className="text-danger">{errors.speciality}</small>}
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <Field label="Note Template" required />
                  <Select
                    options={tab3NoteTemplates}
                    value={selectedNoteTemplate}
                    onChange={(selected) => {
                      setSelectedNoteTemplate(selected);
                      setErrors((prev) => ({ ...prev, noteTemplate: null }));
                    }}
                    placeholder={tab3TemplatesLoading ? "Loading..." : "Select note template..."}
                    isSearchable
                    isClearable={false}
                    isDisabled={!selectedSpeciality || tab3TemplatesLoading}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                  {errors.noteTemplate && <small className="text-danger">{errors.noteTemplate}</small>}
                </div>
              </div>
              <div className="row g-1 mb-2">
                <div className="col-12 col-md-6">
                  <Field label="JSON Data (TXT only)" required />
                  <div className="form-text text-muted" style={{ fontSize: "11px", marginBottom: "2px" }}>Choose .txt file only</div>
                  <input type="file" ref={jsonFileRef} className="form-control form-control-sm" accept=".txt,text/plain" onChange={handleFileChange(setJsonFile)} />
                  {errors.jsonFile && <small className="text-danger">{errors.jsonFile}</small>}
                </div>
                <div className="col-12 col-md-6">
                  <Field label="Template Document (DOCX only)" required />
                  <div className="form-text text-muted" style={{ fontSize: "11px", marginBottom: "2px" }}>Choose .docx file only</div>
                  <input type="file" ref={docxFileRef} className="form-control form-control-sm" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleFileChange(setDocxFile)} />
                  {errors.docxFile && <small className="text-danger">{errors.docxFile}</small>}
                </div>
              </div>
              <div className="mb-2">
                <Field label="Options" />
                <div className="row g-2 mt-1">
                  {[
                    { key: "doctorSignature", label: "Enable Doctor Signature" },
                    { key: "patientSignature", label: "Enable Patient Signature" },
                    { key: "technicianSignature", label: "Enable Technician Signature" },
                    { key: "diagnosisCode", label: "Enable Diagnosis Code" },
                    { key: "procedureCode", label: "Enable Procedure Code" },
                  ].map(({ key, label }) => (
                    <div key={key} className="col-12 col-sm-6 col-lg-4">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={checkboxes[key]}
                          onChange={() => handleCheckboxChange(key)}
                          style={{ accentColor: "#0aa375" }}
                        />
                        <label className="form-check-label" style={{ fontSize: "12px" }}>{label}</label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="d-flex justify-content-center gap-3 mt-2">
                <button onClick={resetTab3} className="btn btn-outline-secondary btn-sm px-3">Reset</button>
                <button onClick={uploadTemplate} className="btn btn-sm text-white px-3 fw-semibold" style={{ backgroundColor: "#0aa375", border: "none" }}>Upload</button>
              </div>
            </>
          )}

          {activeTab === 4 && (
            <>
              <div className="row g-1 mb-2">
                <div className="col-12 col-md-6 col-lg-4">
                  <Field label="Template Name" required />
                  <input
                    type="text" className="form-control form-control-sm"
                    placeholder="e.g. PM Medical Evaluation 2.8"
                    value={newTemplateName}
                    onChange={(e) => { setNewTemplateName(e.target.value); setErrors((prev) => ({ ...prev, newTemplateName: null })); }}
                  />
                  {errors.newTemplateName && <small className="text-danger">{errors.newTemplateName}</small>}
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                   <Field label="Visit Type" required />
                  <Select
                    options={[
                      { value: 0, label: "0 - Generic" }, { value: 1, label: "1 - Initial Evaluation" },
                      { value: 2, label: "2 - Follow-up" }, { value: 3, label: "3 - Re-evaluation" },
                    ]}
                    value={newVisitType} onChange={handleSelectChange(setNewVisitType)}
                    placeholder="Select visit type..." isSearchable={false} isClearable
                    className="react-select-container" classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                  {errors.newVisitType && <small className="text-danger">{errors.newVisitType}</small>}
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                   <Field label="Note Type ID" required />
                  <Select
                    options={[
                      { value: 1, label: "1 - 1.0 notes" },
                      { value: 2, label: "2 - 2.0 & above notes" },
                    ]}
                    value={newNoteTypeId} onChange={handleSelectChange(setNewNoteTypeId)}
                    placeholder="Select note type ID..." isSearchable={false} isClearable
                    className="react-select-container" classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                  {errors.newNoteTypeId && <small className="text-danger">{errors.newNoteTypeId}</small>}
                </div>
              </div>
              <div className="d-flex justify-content-center gap-3 mt-2">
                <button onClick={resetTab4} className="btn btn-outline-secondary btn-sm px-3">Reset</button>
                <button onClick={createNewTemplate} className="btn btn-sm text-white px-3" style={{ backgroundColor: "#0aa375", border: "none" }}>
                  Create Template
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateConfigurationContent;
